/**
 * Application-wide configuration constants
 * 
 * This file centralizes all magic numbers and configuration values
 * used throughout the application for better maintainability.
 */

/**
 * Pagination configuration
 */
export const PAGINATION = {
  /** Number of items to display per page in lists */
  ITEMS_PER_PAGE: 10,
} as const;

/**
 * Virtual scrolling configuration
 */
export const VIRTUAL_SCROLL = {
  /** Minimum number of items before virtual scrolling is enabled */
  THRESHOLD: 50,
} as const;

/**
 * Debouncing configuration
 */
export const DEBOUNCE = {
  /** Default debounce delay in milliseconds for storage operations */
  STORAGE_DELAY: 500,
  /** Debounce delay for search input */
  SEARCH_DELAY: 300,
} as const;

/**
 * Storage configuration
 */
export const STORAGE = {
  /** Maximum number of time entries to keep before cleanup */
  MAX_TIME_ENTRIES: 1000,
  /** Storage quota usage threshold (0.9 = 90%) before cleanup is triggered */
  CLEANUP_THRESHOLD: 0.9,
} as const;

/**
 * ID generation configuration
 */
export const ID_PREFIXES = {
  /** Prefix for quote IDs (e.g., Q2401-001) */
  QUOTE: 'Q',
  /** Prefix for change order IDs */
  CHANGE_ORDER: 'CO',
  /** Prefix for service job IDs */
  SERVICE: 'S',
} as const;

/**
 * File upload configuration
 */
export const FILE_UPLOAD = {
  /** Maximum file size in bytes (10MB) */
  MAX_SIZE: 10 * 1024 * 1024,
} as const;

/**
 * Cache configuration
 */
export const CACHE = {
  /** Cache time-to-live in milliseconds (5 minutes) */
  TTL: 5 * 60 * 1000,
} as const;

/**
 * Date/time configuration
 */
export const DATE_TIME = {
  /** Number of days before a sent quote should be followed up */
  FOLLOW_UP_DAYS: 7,
  /** Milliseconds in a day */
  MILLISECONDS_PER_DAY: 1000 * 60 * 60 * 24,
} as const;

/**
 * All application configuration constants
 */
export const APP_CONFIG = {
  PAGINATION,
  VIRTUAL_SCROLL,
  DEBOUNCE,
  STORAGE,
  ID_PREFIXES,
  FILE_UPLOAD,
  CACHE,
  DATE_TIME,
} as const;

